import { newId } from "@helmr/contracts";
import { useSyncExternalStore } from "react";
import type { PickerEntry } from "./auth";

/** Where the token lives. Web: localStorage. Native: secure storage through the bridge (app-native). */
export interface TokenStore {
  read(): StoredSession | null;
  write(value: StoredSession | null): void;
  deviceId(): string;
}

export interface StoredSession {
  token: string;
  expiresAt: number;
  stage: "identity" | "scoped";
  businessId?: string;
  branchId?: string;
  staffId?: string;
  roleId?: string;
}

const KEY = "helmr.session";
const DEVICE_KEY = "helmr.deviceId";

export function localTokenStore(storage: Storage = globalThis.localStorage): TokenStore {
  return {
    read() {
      try {
        const raw = storage.getItem(KEY);
        return raw ? (JSON.parse(raw) as StoredSession) : null;
      } catch {
        return null;
      }
    },
    write(value) {
      if (value) storage.setItem(KEY, JSON.stringify(value));
      else storage.removeItem(KEY);
    },
    deviceId() {
      // Generated once per install with the `device` id prefix; kept with the token.
      let id = storage.getItem(DEVICE_KEY);
      if (!id) {
        id = newId("device");
        storage.setItem(DEVICE_KEY, id);
      }
      return id;
    },
  };
}

export function memoryTokenStore(initial: StoredSession | null = null): TokenStore {
  let current = initial;
  const device = newId("device");
  return { read: () => current, write: (v) => { current = v; }, deviceId: () => device };
}

export type BranchFilter = string | "all";

export interface SessionState {
  status: "signed_out" | "identity" | "scoped";
  token: string | null;
  businessId: string | null;
  /** The branch the token is scoped to. */
  branchId: string | null;
  staffId: string | null;
  /** Which branch the lists show: the session branch, or "all" for multi-branch roles. */
  branchFilter: BranchFilter;
  /** Where to go after sign-in. */
  returnTo: string | null;
  picker: PickerEntry[];
}

const SIGNED_OUT: SessionState = { status: "signed_out", token: null, businessId: null, branchId: null, staffId: null, branchFilter: "all", returnTo: null, picker: [] };

export interface SessionStore {
  getState(): SessionState;
  subscribe(fn: () => void): () => void;
  deviceId(): string;
  setIdentity(v: { token: string; expiresAt: number }, picker: PickerEntry[]): void;
  setScoped(v: { token: string; expiresAt: number; businessId: string; branchId: string; staffId?: string; roleId?: string }): void;
  setPicker(picker: PickerEntry[]): void;
  setBranchFilter(filter: BranchFilter): void;
  /** Clears the token. `returnTo` is kept so the user lands back where they were. */
  signOut(returnTo?: string | null): void;
  clearReturnTo(): void;
}

export function createSessionStore(tokens: TokenStore): SessionStore {
  const listeners = new Set<() => void>();
  const stored = tokens.read();
  let state: SessionState = stored
    ? {
        ...SIGNED_OUT,
        status: stored.stage,
        token: stored.token,
        businessId: stored.businessId ?? null,
        branchId: stored.branchId ?? null,
        staffId: stored.staffId ?? null,
        branchFilter: stored.branchId ?? "all",
      }
    : SIGNED_OUT;
  const set = (next: SessionState) => {
    state = next;
    listeners.forEach((l) => l());
  };
  return {
    getState: () => state,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    deviceId: () => tokens.deviceId(),
    setIdentity(v, picker) {
      tokens.write({ token: v.token, expiresAt: v.expiresAt, stage: "identity" });
      set({ ...state, status: "identity", token: v.token, businessId: null, branchId: null, staffId: null, branchFilter: "all", picker });
    },
    setScoped(v) {
      tokens.write({ token: v.token, expiresAt: v.expiresAt, stage: "scoped", businessId: v.businessId, branchId: v.branchId, staffId: v.staffId, roleId: v.roleId });
      set({ ...state, status: "scoped", token: v.token, businessId: v.businessId, branchId: v.branchId, staffId: v.staffId ?? null, branchFilter: v.branchId });
    },
    setPicker: (picker) => set({ ...state, picker }),
    setBranchFilter: (branchFilter) => set({ ...state, branchFilter }),
    signOut(returnTo) {
      tokens.write(null);
      set({ ...SIGNED_OUT, returnTo: returnTo ?? state.returnTo });
    },
    clearReturnTo: () => set({ ...state, returnTo: null }),
  };
}

export function useSessionState(store: SessionStore): SessionState {
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}
